
// ##################################################

import React, { useEffect, useRef } from "react";
import { Card, Grid, Tooltip } from "@mui/material"; // Added Tooltip
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { charts, checkChartRequirements } from "layouts/insight-hub";

// Icons
import GroupIcon from "@mui/icons-material/Group"; // Audience
import TouchAppIcon from "@mui/icons-material/TouchApp"; // Engagement
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions"; // Sentiment
import ArticleIcon from "@mui/icons-material/Article"; // Content
import PersonPinIcon from "@mui/icons-material/PersonPin"; // Demographics
import DashboardIcon from "@mui/icons-material/Dashboard"; // All Charts

const getCategoryIcon = (category) => {
  switch (category?.toLowerCase()) {
    case "audience":
      return <GroupIcon fontSize="medium" />;
    case "engagement":
      return <TouchAppIcon fontSize="medium" />;
    case "sentiment":
      return <EmojiEmotionsIcon fontSize="medium" />;
    case "content":
      return <ArticleIcon fontSize="medium" />;
    case "demographics":
      return <PersonPinIcon fontSize="medium" />;
    case "all":
      return <DashboardIcon fontSize="medium" />;
    default:
      return <DashboardIcon fontSize="medium" />;
  }
};

const ChartCategorySelector = ({ selectedCategories, setSelectedCategories, selectedProfiles }) => { // ✅ Receive selectedProfiles
  const categories = [
    ...new Set(
      charts
        ?.map((chart) => chart.category)
        ?.filter((cat) => {
          if (!cat || cat.toLowerCase() === "summary") return false;
          // ✅ Filter logic: Show category ONLY if at least one chart is valid
          return charts
            .filter(c => c.category === cat)
            .some(c => checkChartRequirements(c, selectedProfiles || []));
        }) ?? []
    ),
  ];

  const selectedSafe = selectedCategories ?? [];
  const initialized = useRef(false);

  // 🟢 Select ALL categories by default (once on initial load)
  useEffect(() => {
    if (!initialized.current && categories.length > 0) {
      setSelectedCategories(categories);
      initialized.current = true;
    }
  }, [categories, setSelectedCategories]);

  // 🟢 NEW: Auto-select valid categories when profiles change
  // This ensures that if "Demographics" becomes valid, it gets selected automatically.
  useEffect(() => {
    if (initialized.current) {
      // Recalculate valid categories based on current profiles
      const validCategories = [
        ...new Set(
          charts
            ?.map((chart) => chart.category)
            ?.filter((cat) => {
              if (!cat || cat.toLowerCase() === "summary") return false;
              return charts
                .filter(c => c.category === cat)
                .some(c => checkChartRequirements(c, selectedProfiles || []));
            }) ?? []
        ),
      ];
      setSelectedCategories(validCategories);
    }
  }, [selectedProfiles, setSelectedCategories]);

  // 🟢 Handle selection toggle
  const handleToggle = (category) => {
    if (category === "all") {
      const allSelected =
        selectedSafe.length === categories.length && categories.length > 0;
      // ✅ Fix: toggle between "all" or "none"
      setSelectedCategories(allSelected ? [] : categories);
    } else {
      if (selectedSafe.includes(category)) {
        setSelectedCategories(selectedSafe.filter((c) => c !== category));
      } else {
        setSelectedCategories([...selectedSafe, category]);
      }
    }
  };

  const allSelected =
    selectedSafe.length === categories.length && categories.length > 0;

  return (
    <MDBox>
      <MDTypography
        variant="button"
        fontWeight="medium"
        color="text"
        sx={{ marginBottom: 1, display: "block", textAlign: "center", }}
      >
        Chart Categories
      </MDTypography>
      <MDBox
        bgColor="#f7f7f7" // light grey background
        sx={{
          borderRadius: "16px",
          backgroundColor: "#f0f2f5",
          padding: "14px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Grid container spacing={2} justifyContent="center">
          {/* “All Charts” card moved to start */}
          <Grid item>
            <Tooltip title="All Charts" arrow placement="bottom">
              <Card
                onClick={() => handleToggle("all")}
                sx={{
                  cursor: "pointer",
                  width: 48,
                  height: 48,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "12px",
                  boxShadow: allSelected ? 6 : 1,
                  border: allSelected ? "2px solid #00A1A1" : "1px solid #ddd",
                  backgroundColor: allSelected ? "grey.400" : "grey.100",
                  color: allSelected ? "black" : "text.primary",
                  position: "relative",
                  transition: "all 0.3s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 4,
                  },
                }}
              >
                {/* Render All Icon */}
                {getCategoryIcon("all")}

                {allSelected && (
                  <MDBox
                    sx={{
                      position: "absolute",
                      top: -4,
                      right: -4,
                      backgroundColor: "#26282b",
                      borderRadius: "50%",
                      width: 16,
                      height: 16,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 10
                    }}
                  >
                    <CheckCircleIcon color="accent" sx={{ fontSize: 12 }} />
                  </MDBox>
                )}
              </Card>
            </Tooltip>
          </Grid>

          {/* Category cards */}
          {categories.map((category) => {
            const isSelected = selectedSafe.includes(category);

            return (
              <Grid item key={category}>
                <Tooltip title={category} arrow placement="bottom">
                  <Card
                    onClick={() => handleToggle(category)}
                    sx={{
                      cursor: "pointer",
                      width: 48,
                      height: 48,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "12px",
                      boxShadow: isSelected ? 6 : 1,
                      border: isSelected ? "2px solid #00A1A1" : "1px solid #ddd",
                      backgroundColor: isSelected ? "grey.400" : "grey.100",
                      color: isSelected ? "black" : "text.primary",
                      position: "relative",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 4,
                      },
                    }}
                  >
                    {/* Render Icon based on category */}
                    {getCategoryIcon(category)}

                    {/* ✅ Check mark when selected */}
                    {isSelected && (
                      <MDBox
                        sx={{
                          position: "absolute",
                          top: -4,
                          right: -4,
                          backgroundColor: "#26282b",
                          borderRadius: "50%",
                          width: 16,
                          height: 16,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          zIndex: 10
                        }}
                      >
                        <CheckCircleIcon color="accent" sx={{ fontSize: 12 }} />
                      </MDBox>
                    )}
                  </Card>
                </Tooltip>
              </Grid>
            );
          })}


        </Grid>
      </MDBox>
    </MDBox>
  );
};

export default ChartCategorySelector;
